import React, { useState } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const PRIORITY_CONFIG = {
  high: { label: "High", color: "var(--color-error)", bg: "rgba(220,38,38,0.1)" },
  medium: { label: "Medium", color: "var(--color-warning)", bg: "rgba(217,119,6,0.1)" },
  low: { label: "Low", color: "var(--color-success)", bg: "rgba(5,150,105,0.1)" },
};

const STATUS_CONFIG = {
  todo: { label: "To Do", icon: "Circle" },
  "in-progress": { label: "In Progress", icon: "Clock" },
  done: { label: "Done", icon: "CheckCircle2" },
  blocked: { label: "Blocked", icon: "AlertCircle" },
};

export default function TasksPanel({ tasks = [], onStatusChange, onAddTask, isCoInvestor = false }) {
  const [filter, setFilter] = useState("all");

  const filtered = filter === "all" ? tasks : tasks?.filter((t) => t?.status === filter);

  const getStatusColor = (status) => {
    if (status === "done") return "var(--color-success)";
    if (status === "blocked") return "var(--color-error)";
    if (status === "in-progress") return "var(--color-warning)";
    return "var(--color-muted-foreground)";
  };

  if (isCoInvestor) {
    return (
      <div className="card flex flex-col h-full items-center justify-center text-center py-12">
        <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ background: "rgba(59,76,76,0.08)" }}>
          <Icon name="Lock" size={18} color="var(--color-primary)" />
        </div>
        <p className="font-caption text-sm font-semibold text-foreground mb-1">Tasks restricted</p>
        <p className="font-caption text-xs text-muted-foreground max-w-[200px] leading-relaxed">
          Task management is restricted to the deal team.
        </p>
      </div>
    );
  }

  return (
    <div className="card flex flex-col h-full">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-base font-semibold text-foreground">Tasks</h3>
        <Button variant="outline" size="sm" iconName="Plus" iconPosition="left" iconSize={14} onClick={onAddTask}>
          Add Task
        </Button>
      </div>
      <div className="flex gap-1.5 flex-wrap mb-3">
        {["all", "todo", "in-progress", "blocked"]?.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`font-caption text-xs px-2.5 py-1 rounded-full border transition-all duration-200 capitalize ${
              filter === s
                ? "border-primary text-primary-foreground"
                : "border-border text-muted-foreground hover:border-primary hover:text-primary"
            }`}
            style={filter === s ? { background: "var(--color-primary)", borderColor: "var(--color-primary)" } : {}}
          >
            {s === "all" ? "All" : STATUS_CONFIG?.[s]?.label || s}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
        {filtered?.map((task) => {
          const statusCfg = STATUS_CONFIG?.[task?.status] || STATUS_CONFIG?.todo;
          const priorityCfg = PRIORITY_CONFIG?.[task?.priority] || PRIORITY_CONFIG?.medium;
          return (
            <div key={task?.id} className="p-3 rounded-lg border border-border hover:bg-muted transition-all duration-200">
              <div className="flex items-start gap-2">
                <button
                  onClick={() => onStatusChange && onStatusChange(task?.id)}
                  className="mt-0.5 flex-shrink-0"
                  aria-label={`Mark task ${task?.title} as done`}
                >
                  <Icon
                    name={statusCfg?.icon}
                    size={16}
                    color={getStatusColor(task?.status)}
                    strokeWidth={task?.status === "done" ? 2.5 : 2}
                  />
                </button>
                <div className="flex-1 min-w-0">
                  <p
                    className={`font-caption text-sm font-medium ${
                      task?.status === "done" ? "line-through text-muted-foreground" : "text-foreground"
                    }`}
                  >
                    {task?.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span
                      className="font-caption text-xs px-1.5 py-0.5 rounded"
                      style={{ background: priorityCfg?.bg, color: priorityCfg?.color }}
                    >
                      {priorityCfg?.label}
                    </span>
                    <span className="font-caption text-xs text-muted-foreground flex items-center gap-1">
                      <Icon name="User" size={11} />
                      {task?.assignee}
                    </span>
                    <span className="font-caption text-xs text-muted-foreground flex items-center gap-1">
                      <Icon name="Calendar" size={11} />
                      {task?.dueDate}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {filtered?.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Icon name="CheckCircle2" size={32} color="var(--color-muted-foreground)" />
            <p className="font-caption text-sm text-muted-foreground mt-2">No tasks in this category</p>
          </div>
        )}
      </div>
      <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
        <span className="font-caption text-xs text-muted-foreground">
          {tasks?.filter((t) => t?.status === "done")?.length}/{tasks?.length} completed
        </span>
        <span className="font-caption text-xs text-muted-foreground">
          {tasks?.filter((t) => t?.status === "blocked")?.length} blocked
        </span>
      </div>
    </div>
  );
}